// features/findings/positive-case-search.js
// ════════════════════════════════════════════════════════════════════
// LAB FINDING — Population Decline's Positive-Case Search (v1)
// Form 3 (Falsification test). Pre-registered 2026-05-07.
// Operationalizes the Korea-Hungary thesis as a public falsifier:
// no OECD sub-replacement country produces cohort fertility ≥ 1.70
// for women born 1990-1995 via primarily-policy means by 2035.
//
// Sections that depend on resolution data are marked PENDING with
// resolution mechanics committed up front (anti-goalpost discipline).
// ════════════════════════════════════════════════════════════════════

import {
  FindingBack,
  FindingHeader,
  FindingSection,
  FindingSubhead,
  FindingPill,
  FindingPillRow,
  FindingCite,
} from './components-shared';
import {
  DivergenceTimeline,
  CountryScatter,
  TornadoPlot,
  PredictionsBlock,
  MagnitudeBars,
} from './charts';

// ─── Chart data ─────────────────────────────────────────────────────

// Eight watch-list countries — period TFR trajectories 2010-2024.
// Numbers approximate, drawn from OECD Family Database / Eurostat /
// national statistics agencies. Israel intentionally included as the
// "high-TFR positive case where the wedge is religious-community
// structure, not policy" — listed here to make the wedge contrast
// visible.
const TFR_TRAJECTORIES = [
  {
    id: 'israel',
    label: 'Israel',
    color: 'sage',
    points: [
      { x: 2010, y: 3.03 },
      { x: 2014, y: 3.08 },
      { x: 2018, y: 3.09 },
      { x: 2021, y: 3.00 },
      { x: 2024, y: 2.89 },
    ],
  },
  {
    id: 'france',
    label: 'France',
    color: 'brass',
    points: [
      { x: 2010, y: 2.03 },
      { x: 2014, y: 2.00 },
      { x: 2018, y: 1.88 },
      { x: 2021, y: 1.83 },
      { x: 2024, y: 1.66 },
    ],
  },
  {
    id: 'hungary',
    label: 'Hungary',
    color: 'cognac',
    points: [
      { x: 2010, y: 1.25 },
      { x: 2014, y: 1.41 },
      { x: 2018, y: 1.55 },
      { x: 2021, y: 1.59 },
      { x: 2024, y: 1.38 },
    ],
  },
  {
    id: 'czechia',
    label: 'Czechia',
    color: 'plum',
    points: [
      { x: 2010, y: 1.49 },
      { x: 2014, y: 1.53 },
      { x: 2018, y: 1.71 },
      { x: 2021, y: 1.83 },
      { x: 2024, y: 1.45 },
    ],
  },
  {
    id: 'sweden',
    label: 'Sweden',
    color: 'forest-moss',
    points: [
      { x: 2010, y: 1.99 },
      { x: 2014, y: 1.88 },
      { x: 2018, y: 1.76 },
      { x: 2021, y: 1.67 },
      { x: 2024, y: 1.45 },
    ],
  },
  {
    id: 'germany',
    label: 'Germany',
    color: 'aged-bronze',
    points: [
      { x: 2010, y: 1.39 },
      { x: 2014, y: 1.47 },
      { x: 2018, y: 1.57 },
      { x: 2021, y: 1.58 },
      { x: 2024, y: 1.36 },
    ],
  },
  {
    id: 'japan',
    label: 'Japan',
    color: 'deep-teal',
    points: [
      { x: 2010, y: 1.39 },
      { x: 2014, y: 1.42 },
      { x: 2018, y: 1.42 },
      { x: 2021, y: 1.30 },
      { x: 2024, y: 1.20 },
    ],
  },
  {
    id: 'korea',
    label: 'Korea',
    color: 'oxblood',
    points: [
      { x: 2010, y: 1.23 },
      { x: 2014, y: 1.21 },
      { x: 2018, y: 0.98 },
      { x: 2021, y: 0.81 },
      { x: 2024, y: 0.75 },
    ],
  },
];

// Pronatalist spending (% GDP, 2024) × period TFR change 2010-2024.
// One axis at a time: if spending were the lever, the cluster would
// trend up-and-to-the-right. It doesn't. Israel is the high-TFR outlier
// driven by religious-community structure — the structural-cultural
// substrate, not policy. The point of the chart is the absence of a
// positive-case datum in the upper right.
const SPENDING_TFR_SCATTER = [
  { label: 'Korea',    x: 1.65, y: -0.48, tone: 'oxblood' },
  { label: 'Hungary',  x: 4.20, y:  0.13, tone: 'brass' },
  { label: 'France',   x: 2.91, y: -0.37, tone: 'neutral' },
  { label: 'Sweden',   x: 3.27, y: -0.54, tone: 'neutral' },
  { label: 'Germany',  x: 2.43, y: -0.03, tone: 'neutral' },
  { label: 'Czechia',  x: 2.07, y: -0.04, tone: 'neutral' },
  { label: 'Japan',    x: 1.95, y: -0.19, tone: 'neutral' },
  { label: 'Italy',    x: 2.10, y: -0.16, tone: 'neutral' },
  { label: 'Spain',    x: 1.30, y: -0.05, tone: 'neutral' },
  { label: 'Israel',   x: 1.10, y: -0.14, tone: 'sage', outlier: true, labelDx: -52, labelDy: -8 },
];

// Falsifier sensitivity — varying what we'd count as a save.
const PCS_FALSIFIER_DIMS = [
  {
    id: 'cohort-threshold',
    label: 'Cohort fertility threshold for "recovery"',
    low: -0.20,
    high: 0.20,
    lowLabel: '≥1.70 (committed bar)',
    highLabel: '≥1.85 (replacement-band bar — finding becomes harder to falsify)',
  },
  {
    id: 'wedge-attribution',
    label: 'How clean must the policy-wedge attribution be?',
    low: -0.25,
    high: 0.15,
    lowLabel: 'strict (policy-isolable, no cultural co-shift)',
    highLabel: 'loose (any country with policy expansion counts) — finding overturns more readily',
  },
  {
    id: 'cohort-window',
    label: 'Birth-cohort window for resolution',
    low: -0.10,
    high: 0.20,
    lowLabel: '1990-1995 (committed)',
    highLabel: '1985-2000 (looser — opens older cohorts that completed pre-policy)',
  },
  {
    id: 'tempo-period-substitute',
    label: 'Allow tempo-adjusted period TFR as substitute for cohort?',
    low: -0.15,
    high: 0.20,
    lowLabel: 'no — cohort completion only',
    highLabel: 'yes — tempo-adjusted period TFR ≥1.70 for 5y counts',
  },
];

// Resolution-bar magnitude visualization — how high the falsifier
// threshold sits relative to current cohort trajectories.
const COHORT_TRAJECTORY_BARS = [
  { label: 'Israel cohort (proj — does NOT falsify, wedge is structural)', value: 2.85, tone: 'sage' },
  { label: 'Falsifier bar (committed)', value: 1.70, tone: 'oxblood' },
  { label: 'Sweden cohort (proj)',      value: 1.65, tone: 'forest-moss' },
  { label: 'France cohort (proj)',      value: 1.62, tone: 'brass' },
  { label: 'Czechia cohort (proj)',     value: 1.53, tone: 'plum' },
  { label: 'Germany cohort (proj)',     value: 1.50, tone: 'aged-bronze' },
  { label: 'Hungary cohort (proj)',     value: 1.50, tone: 'cognac' },
  { label: 'Japan cohort (proj)',       value: 1.32, tone: 'teal' },
  { label: 'Korea cohort (proj)',       value: 0.97, tone: 'oxblood' },
];

export const PCS_PREDICTIONS = [
  {
    label: 'Meta-prediction: zero OECD sub-replacement countries produce cohort fertility ≥1.70 for women born 1990-1995 via primarily-policy means by 2035.',
    central: 0,
    band: [0, 0],
    falsifierBand: [0, 1],
    resolutionDate: '2035 cohort completion',
    status: 'open',
    note: 'Single-falsifier-suffices design. One clean policy-driven positive case overturns the necessary-but-insufficient framing.',
  },
  {
    label: 'France cohort fertility (1990-1995 cohort, ~2030 completion)',
    band: [1.55, 1.70],
    central: 1.62,
    falsifierBand: [1.50, 1.80],
    resolutionDate: '2030+ Insee cohort tables',
    status: 'open',
    note: 'France is the historical pronatalist exemplar. Period TFR has fallen 2.03→1.66 across 2010-2024. Cohort projection lands below the falsifier bar despite sustained ~3% GDP family policy.',
  },
  {
    label: 'Sweden cohort fertility (1990-1995 cohort, ~2030 completion)',
    band: [1.60, 1.75],
    central: 1.65,
    falsifierBand: [1.55, 1.85],
    resolutionDate: '2030+ SCB cohort tables',
    status: 'open',
    note: 'Sweden often cited as 1980s pronatalist success. Cohort lands near but below 1.70. Critical: even if it crosses the threshold, the wedge attribution is contested (gender + welfare + cultural shifted in concert) — would be a partial falsification at best.',
  },
  {
    label: 'Hungary cohort fertility (1990-1995 cohort, ~2030 completion)',
    band: [1.45, 1.55],
    central: 1.50,
    falsifierBand: [1.40, 1.70],
    resolutionDate: '2030+ KSH cohort tables',
    status: 'open',
    note: 'Tests whether the 2010-2021 period TFR rise translated into completed cohort fertility. The Korea-Hungary finding’s tempo-without-cohort interpretation predicts no.',
  },
  {
    label: 'Czechia cohort fertility (1990-1995 cohort, ~2030 completion)',
    band: [1.45, 1.60],
    central: 1.53,
    falsifierBand: [1.35, 1.75],
    resolutionDate: '2030+ CZSO cohort tables',
    status: 'open',
    note: 'Czechia’s 2010-2021 uptick was substantial in period terms but disproportionately tempo-driven (catch-up from 1990s postponement). Cohort projection lands below the falsifier bar.',
  },
  {
    label: 'Germany cohort fertility (1990-1995 cohort, ~2030 completion)',
    band: [1.45, 1.55],
    central: 1.50,
    falsifierBand: [1.35, 1.70],
    resolutionDate: '2030+ Destatis cohort tables',
    status: 'open',
    note: 'Germany’s 2008-2021 stabilization is well-documented but did not produce a substantial cohort lift. Migration cohort interactions complicate the wedge attribution.',
  },
  {
    label: 'Japan cohort fertility (1990-1995 cohort, ~2030 completion)',
    band: [1.25, 1.40],
    central: 1.32,
    falsifierBand: [1.15, 1.55],
    resolutionDate: '2030+ MHLW cohort tables',
    status: 'open',
    note: 'Japan’s family-policy expansion accelerated post-2015 but the cohort still tracks the long-run decline. The locking branches in marriage/employment institutions are the binding constraint.',
  },
  {
    label: 'Israel cohort fertility (1990-1995 cohort, ~2030 completion)',
    central: 2.85,
    band: [2.70, 3.05],
    falsifierBand: [2.40, 3.20],
    resolutionDate: '2030+ ICBS cohort tables',
    status: 'open',
    note: 'Predicted ABOVE 1.70 — but this does NOT falsify the meta-prediction. The wedge is religious-community structure (Haredi, religious Zionist), not policy spend. Documents the thesis: structural-cultural substrate is the lever; policy is downstream.',
  },
];

export const FINDING_META = {
  slug: 'positive-case-search',
  domain: 'popdec',
  year: 2026,
  version: 'v1',
  lifecycle: 'pre-registered',
  prevVersion: null,
  revisedBy: null,
  whatChanged: null,
  retractionNote: null,
  citationHandle: 'lab:finding/popdec/2026/positive-case-search/v1',
  title: 'Population Decline’s Positive-Case Search: A Pre-Registered Falsification Test',
  shortTitle: 'Positive-Case Search',
  pageTitle: 'Positive-Case Search — Lab Finding · Shift',
  description: 'A public falsifier register for the necessary-but-insufficient thesis. Eight OECD candidates on the watch list. Resolution: 2030-2035 cohort completion data. Falsifier: any country crossing 1.70 cohort fertility where the wedge is policy spend, not structural-cultural conditions.',
  form: 3,
  formName: 'Falsification test',
  reflexivity: 'HIGH',
  epistemicStatus: 'mechanism',
  confidence: 'medium',
  sampleSize: 8,
  preregisteredAt: '2026-05-07',
  visibility: 'public-peek',
};

export default function PositiveCaseSearchFinding() {
  return (
    <article className="finding-article">
      <FindingBack href="/findings" label="← Findings index" />

      <FindingHeader
        eyebrow="§ Lab finding · popdec/2026/positive-case-search/v1"
        title={FINDING_META.title}
        sub="A public falsifier register for the necessary-but-insufficient thesis."
        subItalic="Eight OECD candidates. The bands committed before resolution. Anti-goalpost rules pre-registered. Resolution: the 1990-1995 cohort completes around 2030-2035. If a single country crosses 1.70 cohort fertility where the wedge is policy spend rather than structural-cultural conditions, the finding overturns."
        meta={FINDING_META}
      />

      <section className="finding-lede">
        <p>
          The Korea-Hungary pair documented something the pronatalist policy debate has been slow to absorb: spending compensates at margins, it cannot create the cultural-structural conditions that lift fertility. To turn that from a written claim into a falsifiable finding, we need a registered prediction with a date on it. This is that prediction. Eight OECD sub-replacement countries are on the watch list. Their 1990-1995 birth cohort completes fertility around 2030-2035. By that resolution, the finding predicts <em>zero clean positive cases</em>. If even one country crosses 1.70 cohort fertility where the wedge is identifiably policy spend rather than structural-cultural conditions, the finding overturns. Below: the bands, the candidates, what we are explicitly not going to count as a save.
        </p>
      </section>

      <section className="finding-tldr">
        <div className="finding-tldr-eyebrow">TL;DR</div>
        <p className="finding-tldr-verdict">
          <strong>Pre-registered:</strong> by the 1990-1995 cohort completion (2030-2035), zero OECD sub-replacement countries will produce cohort fertility ≥ 1.70 via primarily-policy means. A single clean policy-driven positive case overturns the finding. Israel’s high TFR does not count: the wedge is religious-community structure, not policy.
        </p>
        <FindingPillRow>
          <FindingPill tone="mechanism">mechanism documented</FindingPill>
          <FindingPill tone="warn">Confidence: medium</FindingPill>
          <FindingPill tone="neutral">N=8 candidates</FindingPill>
          <FindingPill tone="neutral">Pre-registered 2026-05-07</FindingPill>
          <FindingPill tone="failure">Reflexivity: HIGH</FindingPill>
        </FindingPillRow>
        <div className="finding-tldr-corpus">
          <strong>Corpus:</strong> France, Sweden, Hungary, Czechia, Germany, Japan, Korea, Israel. Sources: OECD Family Database, Human Fertility Database, Eurostat, national statistics agencies. Cross-cite: <FindingCite handle="lab:finding/popdec/2026/korea-hungary-divergence/v1" /> (the static version of this claim) and <FindingCite handle="lab:finding/popdec/2026/korea-lockin/v1" /> (the dynamic version).
        </div>
      </section>

      {/* ── §1 Pre-registered hypothesis ── */}
      <FindingSection
        eyebrow="§ The pre-registered hypothesis"
        title="Verbatim from the registry."
      >
        <p className="finding-prose">
          <strong>Pre-registered 2026-05-07:</strong>
        </p>
        <blockquote className="finding-quote">
          No OECD country currently in sub-replacement fertility (period TFR &lt; 1.7 in 2024) will produce sustained cohort fertility recovery to ≥ 1.70 for women born 1990-1995 via primarily-policy means — defined as pronatalist spending and family-policy expansion absent structural-cultural shift in the housing/working-hours/gender-norms/neighborhood-child-density/intergenerational-caregiving substrate — over the 2025-2035 observation window.
        </blockquote>
        <p className="finding-prose">
          <strong>Falsifier (any one of):</strong>
        </p>
        <ol className="finding-numbered-list">
          <li>Cohort fertility ≥ 1.70 for women born 1990-1995 (cohort completion data ~2030-2035, varying by country) in any country that was sub-replacement at start of window AND where the dominant wedge variable is identifiable as policy spend rather than structural-cultural shift.</li>
          <li>Tempo-adjusted period TFR (Bongaarts-Feeney) sustained ≥ 1.70 for 5 consecutive years across 2025-2035 in any sub-replacement country with the same wedge profile.</li>
          <li>A pronatalist policy package implemented within the window that produces, by 2035, &gt; 0.40 sustained cohort fertility lift in any single country, attributable post-hoc to policy rather than structural co-shift, by independent demographic analysis (e.g., N-IUSSP, AEI Demographics, Wittgenstein Centre).</li>
        </ol>
        <p className="finding-prose">
          <strong>Resolution:</strong> 2035 OECD Family Database / Human Fertility Database cohort completion releases, supplemented by country-level statistics agencies and independent demographic-research bodies. The verdict will be issued in v2 of this finding with a what-changed banner pointing to v1.
        </p>
      </FindingSection>

      {/* ── §2 The prediction ── */}
      <FindingSection
        eyebrow="§ The prediction it made"
        title="Eight candidates, eight bands, one meta."
      >
        <p className="finding-prose">
          The eight watch-list countries cluster sub-replacement (Israel excepted). Their period TFR trajectories 2010-2024 are below. The falsification test asks whether any of them produces a cohort lift the period series would not predict.
        </p>

        <DivergenceTimeline
          title="Watch-list TFR trajectories 2010-2024 — period TFR, eight candidates, sub-replacement cluster (everyone except Israel) is the test bed."
          series={TFR_TRAJECTORIES}
          xLabel="Year"
          yLabel="TFR"
          yMin={0.5}
          yMax={3.2}
        />

        <p className="finding-prose">
          The scatter below sets pronatalist spending intensity (% GDP, current period) against TFR change 2010-2024. If spending were the lever, the cluster would trend up-and-to-the-right. It does not. Hungary’s small positive ticks the highest spending bracket but is interpretable as tempo (the Korea-Hungary finding documents this). Israel’s high TFR sits far left — low spending — because the wedge is religious-community structure.
        </p>

        <CountryScatter
          title="Pronatalist spending (% GDP, ~2024) vs TFR change 2010-2024 — the missing positive case. The upper-right datum (high spend + sustained TFR rise) does not exist."
          points={SPENDING_TFR_SCATTER}
          xLabel="Spending (% GDP)"
          yLabel="TFR change 2010-2024"
          xMin={0}
          xMax={5}
          yMin={-0.6}
          yMax={0.4}
        />

        <FindingSubhead>Per-country predictions</FindingSubhead>
        <p className="finding-prose">
          The cohort projections below are conservative central estimates derived from the 2024 period TFR trajectory minus tempo-share, plus modest cohort drift assumptions consistent with the literature. The falsifier bands are intentionally wide — wide enough that we cannot dismiss a real cohort lift as noise.
        </p>

        <PredictionsBlock
          title="Pre-registered cohort fertility predictions (1990-1995 cohort)"
          predictions={PCS_PREDICTIONS}
        />

        <p className="finding-prose">
          The Israel prediction is the load-bearing contrast: <strong>predicted above 1.70 but not a falsification</strong>, because the wedge is structural-cultural (religious-community substrate), not policy spend. The prediction architecture forces this distinction explicit. If a reader wants to argue Israel falsifies the finding, they have to argue Israeli pronatalist policy is doing the heavy lifting — a position no demographer of Israel actually holds.
        </p>
      </FindingSection>

      {/* ── §3 What actually happened ── */}
      <FindingSection
        eyebrow="§ What actually happened"
        title="Pending resolution."
      >
        <div className="finding-pending-block">
          <div className="finding-pending-eyebrow">PENDING · resolution 2030-2035</div>
          <p className="finding-prose">
            The 1990-1995 birth cohort completes fertility on a rolling basis 2030-2035 (cohort fertility is conventionally treated as complete at age 45). This section will be filled at resolution with:
          </p>
          <ul className="finding-bullets">
            <li>Actual cohort fertility values for the 1990-1995 cohort, by country, with 95% CIs from each national statistics agency.</li>
            <li>Tempo-adjusted period TFR observations across 2025-2035 (annual), to support intermediate verdicts before cohort completion is final.</li>
            <li>Wedge attribution: for any country crossing 1.70, an independent demographic analysis identifying whether the lift is policy-isolable or co-shifted with structural-cultural variables.</li>
            <li>List of resolved policy expansions during the window (Hungary 2019 expansion already resolved; Korea’s late-2020s package will be in scope; any new candidates implemented 2025-2030).</li>
          </ul>
          <p className="finding-prose">
            <strong>Source priority:</strong> (1) Human Fertility Database cohort tables (highest authority for cohort fertility), (2) OECD Family Database / Eurostat for period TFR + spending data, (3) national statistics agencies for the latest pre-cohort-completion data, (4) independent demographic-research bodies (N-IUSSP, AEI, Wittgenstein Centre, INED) for wedge attribution.
          </p>
        </div>
      </FindingSection>

      {/* ── §4 Verdict ── */}
      <FindingSection
        eyebrow="§ Verdict"
        title="Pending resolution."
      >
        <div className="finding-pending-block">
          <div className="finding-pending-eyebrow">PENDING · to be issued in v2</div>
          <p className="finding-prose">
            The verdict will be one of: <em>held</em> (zero countries cross the threshold; or the only crossings are Israel-class structural-cultural wedges that do not count as policy-driven), <em>overturned</em> (one or more countries produce a clean policy-driven positive case), or <em>partial</em> (a country crosses with contested wedge attribution that demographers do not converge on within 24 months of resolution).
          </p>
          <p className="finding-prose">
            <strong>Pre-committed verdict mechanics:</strong>
          </p>
          <ul className="finding-bullets">
            <li><strong>Held</strong> issues v2 of this finding with lifecycle = <code>published</code>, sample size N=8 (or however many candidates remain in the OECD sub-replacement cluster at resolution), and a results section recording each country’s actual cohort fertility against the pre-registered band.</li>
            <li><strong>Overturned</strong> issues v2 with lifecycle = <code>retracted</code>, a retraction note naming the falsifying country and its wedge attribution, and a forward-research statement on what to study next about that country’s mechanism.</li>
            <li><strong>Partial</strong> issues v2 with lifecycle = <code>revised</code>, narrowing the original claim to the verified subset, and pre-registers a v3 with tighter falsification conditions on the unresolved subset.</li>
          </ul>
        </div>
      </FindingSection>

      {/* ── §5 What this means for the next question ── */}
      <FindingSection
        eyebrow="§ What this means for the next question"
        title="The contingent research path."
      >
        <FindingSubhead>If held</FindingSubhead>
        <p className="finding-prose">
          The thesis hardens from claim to structural feature of the policy space. The next finding becomes a constructive program: <em>the 1990-1995 cohort having completed sub-replacement, what does the demographic adaptation playbook look like?</em> That research direction reframes the field from “how do we restore replacement?” to “how do we structure institutions for permanent population contraction without mass welfare collapse?” — the question Korea, Italy, and Japan are already running natural experiments on. A held verdict is not a counsel of despair; it is a redirection of research dollars from policy-spend optimization to structural-condition rebuilding (housing supply, working-time policy, gender-equity hours-gap reduction, neighborhood child-density restoration) on multi-generational time horizons.
        </p>

        <FindingSubhead>If overturned</FindingSubhead>
        <p className="finding-prose">
          The overturning case becomes the new study target. Form 1 (Detective story) is the right form for the follow-up: which suspect mechanism explains the disconfirming case? Pronatalist spending is unlikely to be the only operative variable even if the policy attribution is clean — something else co-shifted that we did not name in the wedge taxonomy. The retraction note for v2 names that something. The Korea-Hungary v1 finding is jointly re-examined; if its tempo-without-cohort interpretation also fails for the new positive case, the static-version finding moves to revised lifecycle and a v2 incorporates the new mechanism.
        </p>

        <FindingSubhead>If partial</FindingSubhead>
        <p className="finding-prose">
          The most likely actual path: a country crosses the threshold but demographers split on whether the wedge is policy or co-shifted. v2 narrows to the cleanly-held subset; v3 pre-registers tighter conditions on the contested case (e.g., “by 2040 the lift sustains under continued policy AND no further structural shift,” which adds another decade of resolution time but extracts the policy-isolation question).
        </p>
      </FindingSection>

      {/* ── §6 Honest accounting (the goalpost commitment) ── */}
      <FindingSection
        eyebrow="§ Honest accounting"
        title="Anti-goalpost-moving commitments, pre-registered."
      >
        <p className="finding-prose">
          The most common failure mode of falsification claims in social science is moving the goalposts at resolution. The following commitments are made <em>now</em>, while we still don’t know the answer, so that future-us cannot save the finding by quietly redefining the test.
        </p>

        <FindingSubhead>What we are explicitly NOT going to count as a save</FindingSubhead>
        <ul className="finding-bullets">
          <li><strong>Tempo-adjusted period TFR rising temporarily.</strong> The Korea-Hungary finding documented the tempo-without-cohort failure mode. We commit: tempo-adjusted period TFR ≥ 1.70 for fewer than 5 consecutive years does not save the finding. Cohort completion data is the gold standard.</li>
          <li><strong>Cohort projections that are not yet complete.</strong> The 1990-1995 cohort begins reaching age 45 around 2035. Until at least 90% of the cohort has reached age 45, projections are interim. We commit: only completed cohort fertility (or cohort fertility for women aged ≥ 42 with ≤ 5% upward revision plausible) counts as resolution data.</li>
          <li><strong>Country-specific exemptions for measurement difficulty.</strong> Migration creates noise in cohort fertility for Germany, Sweden, and France. We commit: the published cohort fertility from the Human Fertility Database / national statistics agency at the resolution date counts as the official value, regardless of post-hoc adjustment proposals.</li>
          <li><strong>Wedge re-attribution after resolution.</strong> If a country crosses 1.70 and the wedge looks structural-cultural rather than policy, that is a finding-saving condition; we accept that. But: the structural-cultural attribution must be issued by an independent demographic body within 24 months of resolution, not by the Lab itself, and not retroactively expanded to capture borderline cases.</li>
          <li><strong>Outcome-favorable reframing of the candidate set.</strong> The watch list is fixed at 8 countries at v1. If a country we did not include (say, Iran or Tunisia, both in fertility transition) crosses 1.70 in cohort terms, that is interesting but it is not a falsifier of this particular finding (which named OECD sub-replacement countries). v2 will pre-register a separate finding for non-OECD candidates if the question warrants.</li>
        </ul>

        <FindingSubhead>Falsifier sensitivity</FindingSubhead>
        <p className="finding-prose">
          The TornadoPlot below visualizes how the verdict shifts under different (non-pre-registered) bar choices. Reading: lower-bar choices (loose) make the finding easier to overturn; upper-bar choices (strict) make it easier to defend. The committed bar sits at the calibration the original Korea-Hungary thesis named. We bind ourselves to that bar.
        </p>

        <TornadoPlot
          title="Falsifier sensitivity — alternative bar choices. Each row varies one calibration parameter. The committed bar (low end) is what the finding will be tested against."
          dimensions={PCS_FALSIFIER_DIMS}
        />

        <FindingSubhead>Cohort distance from the bar</FindingSubhead>
        <MagnitudeBars
          title="Falsifier bar (1.70) vs central cohort projections [projections derived from 2024 period TFR trajectory minus tempo-share + modest cohort drift assumptions; resolution against actual cohort completion data 2030-2035] — the bar sits well above every committed central estimate except Israel’s, and Israel’s high projection does not falsify (the wedge is structural-cultural, not policy)."
          items={COHORT_TRAJECTORY_BARS}
          valueLabel="cohort fertility (projected)"
        />
      </FindingSection>

      {/* ── §7 What we're watching next ── */}
      <FindingSection
        eyebrow="§ What we’re watching next"
        title="Open questions adjacent to this test."
      >
        <ul className="finding-bullets">
          <li><strong>The structural-cultural substrate question.</strong> If policy is not the lever and structural-cultural conditions are, what specifically about the substrate matters most? Working-hours policy? Housing affordability? Gender hours-gap inside marriages? Neighborhood child-density? Each is independently testable against cross-country variance. The next finding in this domain operationalizes one of them.</li>
          <li><strong>The asymmetry mechanism.</strong> Cultural-structural conditions take generations to build, can be lost in a generation. What specifically produces this asymmetry? Coordination-equilibrium dynamics (everyone has to expect everyone else)? Infrastructure decay outpacing infrastructure construction? Cohort-cohort transmission of expectations? This is the build/destroy asymmetry mentioned in the load-bearing thesis; it deserves its own Form 4 (Pattern across cases) finding.</li>
          <li><strong>Israel as the constructive positive case.</strong> If religious-community structure is the operative wedge, what specifically about that structure is the lever? Marriage timing? Community child-care infrastructure? Status reward for parenthood? Fertility transmission within sub-communities? Pre-registering a constructive (non-falsification) finding on Israel’s fertility mechanism would test whether the structural-cultural lens has positive predictive content, not just negative.</li>
          <li><strong>The 2040+ horizon.</strong> If the 2030-2035 verdict holds, the next pre-registration extends to the 1995-2000 cohort (completing ~2040), which will have lived through a different policy environment (post-Korean lowest-low, post-Hungarian retreat, post-Japan policy expansion). The question for v3 of this finding: does the policy-versus-structural conclusion hold across cohorts that grew up under explicitly more pronatalist regimes?</li>
          <li><strong>Cross-domain replication.</strong> The necessary-but-insufficient framing should generalize beyond fertility. Trust networks, religious participation, civic engagement — all are coordination-equilibrium phenomena where policy can compensate at margins but not create the substrate. A Form 4 finding that documents the asymmetry across multiple substrate phenomena would strengthen the population-decline finding by establishing the mechanism’s broader scope.</li>
        </ul>

        <FindingSubhead>Cross-references</FindingSubhead>
        <p className="finding-prose">
          This finding is the operationalized falsification test of <FindingCite handle="lab:finding/popdec/2026/korea-hungary-divergence/v1" /> (the static thesis) and <FindingCite handle="lab:finding/popdec/2026/korea-lockin/v1" /> (the dynamic thesis). All three should be read together. Reading any one without the others weakens the structural argument; reading them together makes it bulletproof.
        </p>
      </FindingSection>

      <footer className="finding-footer">
        <div className="finding-footer-eyebrow">Footer ledger</div>
        <dl className="finding-meta">
          <dt>Methodology Version</dt><dd>Lab V2.0</dd>
          <dt>Form</dt><dd>Form 3 — Falsification test</dd>
          <dt>Reflexivity</dt><dd>HIGH — widely-cited findings about fertility trajectories can affect the systems being predicted (panic, policy retreat, fertility decisions)</dd>
          <dt>Epistemic status</dt><dd>Mechanism documented · upgrade to causal-chain contingent on resolution outcome</dd>
          <dt>Sample size</dt><dd>N=8 OECD candidates (France, Sweden, Hungary, Czechia, Germany, Japan, Korea, Israel)</dd>
          <dt>Pre-registration date</dt><dd>2026-05-07. Resolution: 2030-2035 cohort completion data.</dd>
          <dt>Visibility</dt><dd>Public · read-only</dd>
          <dt>Citation handle</dt><dd><code>{FINDING_META.citationHandle}</code></dd>
          <dt>Falsifier</dt><dd>Any single OECD sub-replacement country produces cohort fertility ≥ 1.70 for women born 1990-1995 via primarily-policy means by 2035, with independent demographic attribution to policy rather than structural-cultural shift.</dd>
        </dl>
        <div className="finding-citation">
          <strong>Cite as:</strong> Mai, T. (2026). Population Decline’s Positive-Case Search: A Pre-Registered Falsification Test. Lab Finding {FINDING_META.citationHandle}.
        </div>
        <div className="finding-version">
          <div className="finding-footer-eyebrow">Update history</div>
          <p>v1 — 2026-05-07 — initial pre-registration. Resolution: 2030-2035 (rolling) cohort completion data.</p>
        </div>
      </footer>
    </article>
  );
}
